import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";
import GroupDetail from "./GroupDetail";
import BulkImportManagers from "./BulkImportManagers";
import AccessBreadcrumb from "./AccessBreadcrumb";
import {
  SG_GET_ORGANIZATION_GROUP,
  SG_ADD_ORGANIZATION_GROUP,
  SG_DELETE_ORGANIZATION_GROUP,
  LOAD_EMPLOYEES,
  SG_REGISTER_EMPLOYEE_CULTURE,
  SG_UPDATE_EMPLOYEE_AUDIT,
  CLEAR_ORGANIZATION_GROUP,
  UPDATE_EMPLOYEE,
  SG_ADD_EMPLOYEE_LOG,
} from "../../constants/actions";
import { ADD_ORGANIZATION_GROUP, GET_ORGANIZATION_GROUP as API_GET_ORGANIZATION_GROUP } from "../../constants/api";

// Icons (simplified inline SVG components)
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
  </svg>
);

const GroupIcon = () => (
     <svg width="15" height="15" viewBox="0 0 576 512" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M264.5 5.20002C279.4 -1.69998 296.6 -1.69998 311.5 5.20002L530.1 106.2C538.6 110.1 544 118.6 544 128C544 137.4 538.6 145.9 530.1 149.8L311.5 250.8C296.6 257.7 279.4 257.7 264.5 250.8L45.9 149.8C37.4 145.8 32 137.3 32 128C32 118.7 37.4 110.1 45.9 106.2L264.5 5.20002ZM476.9 209.6L530.1 234.2C538.6 238.1 544 246.6 544 256C544 265.4 538.6 273.9 530.1 277.8L311.5 378.8C296.6 385.7 279.4 385.7 264.5 378.8L45.9 277.8C37.4 273.8 32 265.3 32 256C32 246.7 37.4 238.1 45.9 234.2L99.1 209.6L251.1 279.8C262.67 285.136 275.259 287.9 288 287.9C300.741 287.9 313.33 285.136 324.9 279.8L476.9 209.6ZM324.9 407.8L476.9 337.6L530.1 362.2C538.6 366.1 544 374.6 544 384C544 393.4 538.6 401.9 530.1 405.8L311.5 506.8C296.6 513.7 279.4 513.7 264.5 506.8L45.9 405.8C37.4 401.8 32 393.3 32 384C32 374.7 37.4 366.1 45.9 362.2L99.1 337.6L251.1 407.8C262.67 413.136 275.259 415.9 288 415.9C300.741 415.9 313.33 413.136 324.9 407.8Z" fill="currentColor"/>
</svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

// Custom Confirmation Dialog Component
const ConfirmationDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Remove", cancelText = "Cancel" }) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ConfirmationContent onClick={e => e.stopPropagation()}>
        <ConfirmationHeader>
          <ConfirmationTitle>{title}</ConfirmationTitle>
          <CloseButton onClick={onClose}>x</CloseButton>
        </ConfirmationHeader>
        
        <ConfirmationBody>
          <ConfirmationMessage>{message}</ConfirmationMessage>
        </ConfirmationBody>
        
        <ConfirmationActions>
          <CancelButton onClick={onClose}>
            {cancelText}
          </CancelButton>
          <ConfirmButton onClick={onConfirm}>
            {confirmText}
          </ConfirmButton>
        </ConfirmationActions>
      </ConfirmationContent>
    </ModalOverlay>
  );
};

// Add Member Modal Component
const AddMemberModal = ({
  isOpen,
  onClose,
  onSave,
  employees = [],
  organizationGroups = [],
  get_auth,
}) => {
  const [formData, setFormData] = useState({
    email: "",
    first_name: "",
    last_name: "",
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [managerCheckLoading, setManagerCheckLoading] = useState(false);
  const [isAlreadyManagerAPI, setIsAlreadyManagerAPI] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ email: "", first_name: "", last_name: "" })
      setIsProcessing(false)
      setManagerCheckLoading(false)
      setIsAlreadyManagerAPI(false)
      setEmailError("")
    }
  }, [isOpen])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isValidEmail && !emailError && !isProcessing && !isAlreadyManagerAPI) {
      console.log("🔄 Modal submitting with email:", formData.email)
      setIsProcessing(true)
      onSave(formData)
      // Don't close modal yet - wait for processing to complete
    }
  }

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  const handleChange = (field, value) => {
    setFormData((prev) => {
      if (field === "email" && !value.trim()) {
        return { ...prev, [field]: value, first_name: "", last_name: "" }
      }
      return { ...prev, [field]: value }
    })

    // Validate email format
    if (field === "email") {
      if (value.trim() && !emailRegex.test(value.trim())) {
        setEmailError("Please enter a valid email address")
      } else {
        setEmailError("")
      }

      // Clear manager validation state when email is cleared
      if (!value.trim()) {
        setIsAlreadyManagerAPI(false)
        setManagerCheckLoading(false)
      }
    }
  }

  // Check if email exists in employees (only if email is valid)
  const isValidEmail =
    formData.email.trim() && emailRegex.test(formData.email.trim())
  const existingEmployee = isValidEmail
    ? employees.find(
        (emp) => emp.email?.toLowerCase() === formData.email?.toLowerCase()
      )
    : null

  useEffect(() => {
    if (!existingEmployee) return

    setFormData((prev) => {
      const first = existingEmployee.first_name || ""
      const last = existingEmployee.last_name || ""
      const prevFirst = (prev.first_name || "").trim()
      const prevLast = (prev.last_name || "").trim()
      const shouldUpdateFirst = first && prevFirst === ""
      const shouldUpdateLast = last && prevLast === ""

      if (!shouldUpdateFirst && !shouldUpdateLast) {
        return prev
      }

      return {
        ...prev,
        first_name: shouldUpdateFirst ? first : prev.first_name,
        last_name: shouldUpdateLast ? last : prev.last_name,
      }
    })
  }, [
    existingEmployee?.id,
    existingEmployee?.first_name,
    existingEmployee?.last_name,
  ])

  // Check if this employee is already a manager
  const isAlreadyManager =
    existingEmployee &&
    organizationGroups.some((group) => {
      const managerId =
        typeof group.manager === "object" ? group.manager?.id : group.manager
      const isManager = managerId === existingEmployee.id
      if (isManager) {
        console.log("🔍 Modal: Found existing manager match:", {
          group: group,
          managerId: managerId,
          employeeId: existingEmployee.id,
          employee: existingEmployee,
        })
      }
      return isManager
    })

  // Check if employee is archived (status === 99)
  const isArchived = existingEmployee && existingEmployee.status === 99

  console.log("🔍 Modal validation:", {
    existingEmployee: existingEmployee,
    organizationGroups: organizationGroups,
    isAlreadyManager: isAlreadyManager,
    email: formData.email,
  })

  // Check API when existing employee is found
  useEffect(() => {
    const checkManagerStatus = async () => {
      if (!existingEmployee) {
        setIsAlreadyManagerAPI(false)
        return
      }

      setManagerCheckLoading(true)
      setIsAlreadyManagerAPI(false)

      try {
        const orgId =
          get_auth?.organization_id || get_auth?.selectedOrganization?.id
        const response = await API_GET_ORGANIZATION_GROUP({
          manager: existingEmployee.id,
          organization: orgId,
          group_type: "permission",
        })

        const existingGroups = response?.data?.results || response?.data || []
        const isManager = existingGroups.length > 0

        console.log("🔍 Modal API manager check:", {
          employee: existingEmployee,
          existingGroups: existingGroups,
          isManager: isManager,
        })

        setIsAlreadyManagerAPI(isManager)
      } catch (error) {
        console.error("❌ Modal API manager check failed:", error)
        setIsAlreadyManagerAPI(false)
      } finally {
        setManagerCheckLoading(false)
      }
    }

    checkManagerStatus()
  }, [existingEmployee])

  // Close modal when processing is complete
  useEffect(() => {
    if (isProcessing && !isOpen) {
      setIsProcessing(false)
    }
  }, [isOpen, isProcessing])

  if (!isOpen) return null

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Add New User</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel>Email Address *</FormLabel>
              <FormInput
                type="email"
                placeholder="Enter email address..."
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                disabled={isProcessing}
                required
              />
              {emailError && (
                <EmailErrorNote>
                  {emailError}
                </EmailErrorNote>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div>
                  <FormLabel>First Name (optional)</FormLabel>
                  <FormInput
                    type="text"
                    placeholder="First name"
                    value={formData.first_name}
                    onChange={(e) => handleChange("first_name", e.target.value)}
                    disabled={isProcessing}
                  />
                </div>
                <div>
                  <FormLabel>Last Name (optional)</FormLabel>
                  <FormInput
                    type="text"
                    placeholder="Last name"
                    value={formData.last_name}
                    onChange={(e) => handleChange("last_name", e.target.value)}
                    disabled={isProcessing}
                  />
                </div>
              </div>
              {managerCheckLoading && existingEmployee && (
                <ExistingEmployeeNote>
                  Checking if {existingEmployee.first_name}{" "}
                  {existingEmployee.last_name} is already a manager...
                </ExistingEmployeeNote>
              )}
              {isAlreadyManagerAPI && !managerCheckLoading && existingEmployee && (
                <ManagerWarningNote>
                  ⚠️ {existingEmployee.first_name} {existingEmployee.last_name}{" "}
                  is already a group manager
                </ManagerWarningNote>
              )}
              {existingEmployee &&
                !isAlreadyManagerAPI &&
                !managerCheckLoading && (
                  <ExistingEmployeeNote>
                  {isArchived
                    ? `Found archived contact: ${existingEmployee.first_name} ${existingEmployee.last_name} - will be re-activated`
                    : `Found existing contact: ${existingEmployee.first_name} ${existingEmployee.last_name}`}
                  </ExistingEmployeeNote>
                )}
              {/* {formData.email && !existingEmployee && (
                <NewEmployeeNote>
                  Will create new user without login access; permissions can be granted later
                </NewEmployeeNote>
              )} */}
            </FormGroup>

            <ModalActions>
              <CancelButton type="button" onClick={onClose}>
                Cancel
              </CancelButton>
              <SaveButton
                type="submit"
                disabled={
                  !isValidEmail ||
                  emailError ||
                  isProcessing ||
                  isAlreadyManagerAPI ||
                  managerCheckLoading
                }
              >
                {isProcessing
                  ? "Processing..."
                  : managerCheckLoading
                  ? "Checking..."
                  : isAlreadyManagerAPI
                  ? "Already a Manager"
                  : emailError
                  ? "Invalid Email"
                  : existingEmployee
                          ? isArchived
                            ? "Re-activate User"
                            : "Add Existing User"
                          : "Create & Add User"}
              </SaveButton>
            </ModalActions>
          </form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  )
}

const DataAccessManagement = () => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'detail' | 'bulk'
  const [activeTab, setActiveTab] = useState(0); // 0 = Groups, 1 = Login Access
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState("1");
  const [pageSize] = useState(12); // Default page size
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, groupId: null });
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncIndex, setSyncIndex] = useState(0);
  const [syncTotal, setSyncTotal] = useState(0);
  const [syncCurrent, setSyncCurrent] = useState(null);
  const [syncErrors, setSyncErrors] = useState([]);
  const syncCancelledRef = useRef(false);
  const [allManagerIds, setAllManagerIds] = useState(new Set());
  const [allManagersLoading, setAllManagersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedManagerFilter, setSelectedManagerFilter] = useState(null);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [shouldShowTabs, setShouldShowTabs] = useState(false);

  // Redux state
  const {
    organization_groups,
    employees,
    pending,
    get_auth,
    pagination,
    organization_group_detail,
    employeeCreated,
    employeePending,
    errors,
  } = useSelector(
    (state) => ({
      organization_groups: Array.isArray(
        state.organization_group?.organization_groups
      )
        ? state.organization_group.organization_groups
        : [],
      employees: Array.isArray(state.employees?.employees)
        ? state.employees.employees
        : [],
      pending: state.organization_group?.pending || false,
      get_auth: state.auth,
      pagination: state.organization_group?.page || {},
      organization_group_detail: state.organization_group?.organization_group,
      employeeCreated: state.employees?.created,
      employeePending: state.employees?.pending,
      errors: state.errors,
    }),
    shallowEqual
  )

  // Initialize data loading (always request permission group type from API)
  useEffect(() => {
    console.log(
      "🔄 Dispatching SG_GET_ORGANIZATION_GROUP with page:",
      currentPage,
      "pageSize:",
      pageSize,
      "activeTab:",
      activeTab
    )
    // Only make API call for Groups tab or if no activeTab is set (initial load)
    const orgId =
      get_auth?.organization_id || get_auth?.selectedOrganization?.id
    dispatch({
      type: SG_GET_ORGANIZATION_GROUP,
      payload: {
        page: currentPage,
        page_size: pageSize,
        organization: orgId,
        group_type: "permission",
      },
    })

    if (!employees?.length) {
      dispatch({ type: LOAD_EMPLOYEES })
    }
  }, [dispatch, currentPage, pageSize, activeTab])

  // Helper to load ALL permission group managers once per org (for accurate Login Access filtering)
  const fetchAllManagerIds = async () => {
    const orgId =
      get_auth?.organization_id || get_auth?.selectedOrganization?.id
    if (!orgId) return
    setAllManagersLoading(true)
    try {
      const params = {
        organization: orgId,
        group_type: "permission",
        page: 1,
        page_size: 5000,
      }
      const res = await API_GET_ORGANIZATION_GROUP(params)
      const data = res?.data
      const results = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : []
      const ids = new Set(
        results
          .map((g) => g?.manager)
          .filter(Boolean)
          .map((m) => (typeof m === "object" ? m.id : m))
      )
      setAllManagerIds(ids)
    } catch (e) {
      console.error("Failed to load all manager IDs", e)
      setAllManagerIds(new Set())
    } finally {
      setAllManagersLoading(false)
    }
  }

  // Load ALL permission group managers once per org (for accurate Login Access filtering immediately)
  useEffect(() => {
    fetchAllManagerIds()
  }, [get_auth?.organization_id, get_auth?.selectedOrganization?.id])

  // Defer showing tabs for up to 20s to allow login access sync/state to settle.
  // After 20s, only reveal if there are non-manager login-access contacts.
  useEffect(() => {
    const timerId = setTimeout(() => {
      try {
        const managerIds = Array.from(allManagerIds)
        const nonManagerLoginCount = (
          Array.isArray(employees) ? employees : []
        ).filter((emp) => {
          const hasLoginFlag =
            emp.enable_login === true ||
            emp.enable_login === 1 ||
            emp.enable_login === "1"
          const hasPermissionsArray =
            Array.isArray(emp.new_permission) &&
            emp.new_permission.some(
              (p) =>
                (p?.name_id || p?.name) === "has_login" ||
                (p?.name_id || p?.name) === "hasLogin" ||
                (p?.value_id || p?.value) === "has_login"
            )
          const hasLoginAlt =
            emp.has_login === true ||
            emp.has_login === 1 ||
            emp.has_login === "1"
          const hasLogin = hasLoginFlag || hasPermissionsArray || hasLoginAlt
          return hasLogin && !managerIds.includes(emp.id)
        }).length
        if (nonManagerLoginCount > 0) {
          setShouldShowTabs(true)
        }
      } catch (_) {
        // No-op
      }
    }, 20000)
    return () => clearTimeout(timerId)
  }, [employees, allManagerIds])

  // Reveal tabs early if non-manager login-access contacts are detected
  useEffect(() => {
    try {
      const managerIds = Array.from(allManagerIds)
      const nonManagerLoginCount = getEmployeesWithLogin().filter(
        (emp) => !managerIds.includes(emp.id)
      ).length
      if (nonManagerLoginCount > 0) {
        setShouldShowTabs(true)
      }
    } catch (_) {
      // No-op: conservative reveal only
    }
  }, [employees, allManagerIds])

  // Debug pagination data
  useEffect(() => {
    if (pagination) {
      console.log("📊 Pagination data:", pagination)
      console.log("📊 Current organization groups:", organization_groups.length)
    }
  }, [pagination, organization_groups])

  // Listen for employee creation to create group automatically
  useEffect(() => {
    if (employeeCreated?.id && !employeePending && isAddMemberModalOpen) {
      console.log("🎉 Contact created successfully:", employeeCreated)

      // Create group for the newly created employee
      setTimeout(() => {
        createGroupForEmployee(employeeCreated)

        // Refresh contacts list to include the new contact
        dispatch({ type: LOAD_EMPLOYEES })
        // Also refresh manager IDs soon after to ensure Login Access tab de-duplicates
        setTimeout(() => {
          fetchAllManagerIds()
        }, 600)
      }, 500)
    }
  }, [employeeCreated, employeePending, isAddMemberModalOpen])

  // Handle errors when modal is open - watch for errors in Redux state
  useEffect(() => {
    if (isAddMemberModalOpen && errors?.status && errors.status >= 400) {
      addToast("Failed to create user. Please try again, if the problem persists, please contact support.", {
        appearance: "error",
        autoDismiss: true,
      })
      setIsAddMemberModalOpen(false)
    }
  }, [isAddMemberModalOpen, errors, addToast])

  // Keep manager ID set in sync when organization groups list changes
  useEffect(() => {
    // Whenever org groups update, re-evaluate the full manager set used by Login Access tab
    // This ensures a user promoted to manager no longer appears under Login Access.
    fetchAllManagerIds()
  }, [organization_groups])

  // Update selectedGroup when organization_groups are refreshed
  useEffect(() => {
    if (selectedGroup && organization_groups.length > 0) {
      const updatedGroup = organization_groups.find(
        (group) => group.id === selectedGroup.id
      )
      if (updatedGroup) {
        // Always update to ensure we have the latest member data
        console.log("🔄 Updating selectedGroup with fresh data:", updatedGroup)
        setSelectedGroup(updatedGroup)
      }
    }
  }, [organization_groups, selectedGroup])

  // Also update selectedGroup when a single group is fetched (e.g., after bulk updates when paging might exclude it)
  useEffect(() => {
    if (
      selectedGroup &&
      organization_group_detail &&
      organization_group_detail.id === selectedGroup.id
    ) {
      console.log(
        "🔄 Syncing selectedGroup from single-group fetch:",
        organization_group_detail
      )
      setSelectedGroup(organization_group_detail)
    }
  }, [organization_group_detail, selectedGroup])

  // Helper function to get employee by ID or return manager object if it's already populated
  const getEmployeeById = (managerData) => {
    if (!managerData) return null

    // If managerData is already an object with employee details, return it directly
    if (typeof managerData === "object" && managerData.id) {
      return managerData
    }

    // If managerData is just an ID, look it up in the employees array
    if (Array.isArray(employees) && typeof managerData === "number") {
      return employees.find((emp) => emp.id === parseInt(managerData))
    }

    return null
  }

  // Helper function to get employees in a group
  const getEmployeesInGroup = (groupId) => {
    if (!groupId) return []

    // Find the group in organization_groups
    const group = organization_groups.find((g) => g.id === groupId)
    if (!group || !Array.isArray(group.members)) return []

    // Return the members directly from the group
    return group.members
      .map((member) => getEmployeeById(member))
      .filter(Boolean)
  }

  // Helper function to get contacts with login permissions
  const getEmployeesWithLogin = () => {
    if (!Array.isArray(employees)) return []

    return employees.filter((employee) => {
      const hasLoginFlag =
        employee.enable_login === true ||
        employee.enable_login === 1 ||
        employee.enable_login === "1"
      // Normalize nested permission shapes if present
      const hasPermissionsArray =
        Array.isArray(employee.new_permission) &&
        employee.new_permission.some(
          (p) =>
            (p?.name_id || p?.name) === "has_login" ||
            (p?.name_id || p?.name) === "hasLogin" ||
            (p?.value_id || p?.value) === "has_login"
        )
      const hasLoginAlt =
        employee.has_login === true ||
        employee.has_login === 1 ||
        employee.has_login === "1"
      return hasLoginFlag || hasPermissionsArray || hasLoginAlt
    })
  }

  // Helper function to get group items for Groups tab (trust server filter)
  const getGroupItems = () => {
    return (Array.isArray(organization_groups) ? organization_groups : []).map(
      (group) => ({
        type: "group",
        id: group.id,
        data: group,
        manager: getEmployeeById(group.manager),
        created_at: group.created_at,
      })
    )
  }

  // Helper function to get login employees for Login Access tab
  const getLoginEmployeeItems = () => {
    // Get contacts with login who aren't already group managers
    const groupManagerIds = Array.from(allManagerIds)

    const allLoginEmployees = getEmployeesWithLogin()
    console.log("🔍 Found contacts with login:", allLoginEmployees.length)
    console.log("🔍 Group manager IDs:", groupManagerIds)

    const loginEmployees = allLoginEmployees
      .filter((emp) => !groupManagerIds.includes(emp.id))
      .map((emp) => ({
        type: "login_employee",
        id: `emp_${emp.id}`,
        data: emp,
        manager: emp,
        created_at: emp.created_at || new Date().toISOString(),
      }))

    console.log("🔍 Login contacts after filtering:", loginEmployees.length)

    // Sort by creation date
    return loginEmployees.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    )
  }

  // Synchronize Managers - create groups for all login contacts (non-managers)
  const openSyncModal = () => {
    const queue = getLoginEmployeeItems().map((item) => item.data)
    setSyncTotal(queue.length)
    setSyncIndex(0)
    setSyncCurrent(null)
    setSyncErrors([])
    syncCancelledRef.current = false
    setIsSyncModalOpen(true)
    if (queue.length > 0) {
      // ensure manager list is loaded before running
      const maybeRun = () => runSync(queue)
      if (allManagersLoading) {
        const wait = setInterval(() => {
          if (!allManagersLoading) {
            clearInterval(wait)
            maybeRun()
          }
        }, 50)
      } else {
        maybeRun()
      }
    }
  }

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

  const createGroupForEmployeeDirect = async (employee) => {
    const orgId =
      get_auth?.organization_id || get_auth?.selectedOrganization?.id
    let managerName = "Unknown Manager"
    if (employee.first_name && employee.last_name) {
      managerName = `${employee.first_name} ${employee.last_name}`
    } else if (employee.email) {
      managerName = employee.email
    }

    const payload = {
      name: managerName,
      description: "",
      organization: orgId,
      manager_id: employee.id,
      group_type: "permission",
    }
    await ADD_ORGANIZATION_GROUP(payload)
  }

  const runSync = async (queue) => {
    setSyncRunning(true)
    try {
      // Build a local set of already-manager IDs to avoid duplicate calls
      const existingManagerIds = new Set(
        organization_groups
          .map((group) => group.manager)
          .filter(Boolean)
          .map((m) => (typeof m === "object" ? m.id : m))
      )
      const locallyCreated = new Set()

      for (let i = 0; i < queue.length; i++) {
        if (syncCancelledRef.current) break
        const employee = queue[i]
        setSyncCurrent(employee)
        setSyncIndex(i + 1)

        // Skip if already a manager (server-side state) or created in this run
        if (
          existingManagerIds.has(employee.id) ||
          locallyCreated.has(employee.id)
        ) {
          continue
        }

        try {
          await createGroupForEmployeeDirect(employee)
          locallyCreated.add(employee.id)
        } catch (err) {
          setSyncErrors((prev) => [
            ...prev,
            {
              id: employee.id,
              email: employee.email,
              error: err?.response?.data || err?.message,
            },
          ])
        }

        // small pacing delay to avoid hammering the API
        await delay(150)
      }
    } finally {
      setSyncRunning(false)
      // Refresh groups and manager ids after sync completes
      const orgId =
        get_auth?.organization_id || get_auth?.selectedOrganization?.id
      dispatch({
        type: SG_GET_ORGANIZATION_GROUP,
        payload: {
          page: 1,
          page_size: pageSize,
          organization: orgId,
          group_type: "permission",
        },
      })
      setCurrentPage(1)
      await fetchAllManagerIds()
    }
  }

  // Helper function to get current tab items
  const getCurrentTabItems = () => {
    if (activeTab === 0) {
      // Apply manager filter if selected (server fetch already applies when set)
      return getGroupItems()
    } else {
      // For login access, show all non-manager login contacts (search is handled via dropdown)
      return getLoginEmployeeItems()
    }
  }

  // Helper function to get search results for dropdown
  const getSearchResults = () => {
    if (!searchTerm.trim()) return [];
    const allEmployeeItems = (Array.isArray(employees) ? employees : [])
      .map(emp => ({ type: 'employee', id: `emp_${emp.id}`, data: emp, manager: emp, created_at: emp.created_at || new Date().toISOString() }));
    const term = searchTerm.toLowerCase().trim();
    return allEmployeeItems.filter(i => 
      `${i.manager.first_name || ''} ${i.manager.last_name || ''}`.toLowerCase().includes(term) || 
      (i.manager.email || '').toLowerCase().includes(term)
    ).slice(0, 10); // Limit to 10 results
  };

  // Update search results when search term changes
  React.useEffect(() => {
    const results = getSearchResults()
    setSearchResults(results)
    setShowSearchDropdown(results.length > 0 && searchTerm.trim() !== "")
  }, [searchTerm, employees])

  // While tabs are hidden, keep the view on User Access to avoid bouncing
  useEffect(() => {
    const managerIds = Array.from(allManagerIds)
    const nonManagerLoginCount = getEmployeesWithLogin().filter(
      (emp) => !managerIds.includes(emp.id)
    ).length
    if (
      !shouldShowTabs &&
      !allManagersLoading &&
      nonManagerLoginCount === 0 &&
      activeTab !== 0
    ) {
      setActiveTab(0)
    }
  }, [shouldShowTabs, allManagersLoading, employees, allManagerIds, activeTab])

  // Handle creating new member (either existing employee or new employee)
  const handleCreateMember = (memberData) => {
    console.log("🔄 Creating member with email:", memberData.email)

    // Check if email exists in contacts
    const existingEmployee = employees.find(
      (emp) => emp.email?.toLowerCase() === memberData.email?.toLowerCase()
    )

    if (existingEmployee) {
      // Contact exists - create group directly
      console.log("🔄 Found existing contact:", existingEmployee)
      createGroupForEmployee(existingEmployee)
    } else {
      // Employee doesn't exist - create employee first, then group
      console.log('🔄 Creating new employee for email:', memberData.email);
      createEmployeeAndGroup(memberData.email, memberData.first_name, memberData.last_name);
    }
  }

  // Create group for existing employee
  const createGroupForEmployee = async (employee) => {
    // Use API to check if this employee is already a manager
    console.log(
      "🔍 Checking if employee is already a manager via API:",
      employee
    )

    try {
      const orgId =
        get_auth?.organization_id || get_auth?.selectedOrganization?.id
      const response = await API_GET_ORGANIZATION_GROUP({
        manager: employee.id,
        organization: orgId,
        group_type: "permission",
      })

      const existingGroups = response?.data?.results || response?.data || []
      console.log("🔍 API response for manager check:", existingGroups)

      if (existingGroups.length > 0) {
        console.log(
          "🚫 Contact is already a manager of groups:",
          existingGroups
        )
        addToast("Selected contact is already a group manager", {
          appearance: "warning",
          autoDismiss: true,
        })
        return
      }

      console.log("✅ Contact is not a manager, proceeding with group creation")
    } catch (error) {
      console.error("❌ Error checking manager status:", error)
      // If API call fails, proceed but log the error
    }

    // Check if employee is archived (status === 99) and reactivate if needed
    const isArchived = employee.status === 99
    if (isArchived) {
      console.log("🔄 Re-activating archived employee:", employee)
      dispatch({
        type: UPDATE_EMPLOYEE,
        payload: {
          id: employee.id,
          status: 1, // Set to active
        },
      })
      dispatch({
        type: SG_ADD_EMPLOYEE_LOG,
        payload: {
          employee: employee.id,
          type_of_log: 2,
          description: {
            Archived: "Re-activated via User Management",
          },
        },
      })
    }

    // First, ensure the employee has the required permissions to be a manager
    dispatch({
      type: SG_UPDATE_EMPLOYEE_AUDIT,
      payload: {
        id: employee.id,
        // Do not change enable_login here; preserve existing login state
        access_data_analytics: 1,
        access_subfactor_data: 1,
        survey_status: 1, // participate in survey = true
        account_type: 9, // regular user (not admin)
        first_name: employee.first_name,
        last_name: employee.last_name,
      },
    })

    let managerName = "Unknown Manager"
    if (employee.first_name && employee.last_name) {
      managerName = `${employee.first_name} ${employee.last_name}`
    } else if (employee.email) {
      managerName = employee.email
    }

    const payload = {
      name: managerName,
      description: "",
      organization:
        get_auth?.organization_id || get_auth?.selectedOrganization?.id,
      manager_id: employee.id,
      group_type: "permission",
    }

    console.log("🔄 Creating group for existing employee:", payload)

    // Create the group after a brief delay to ensure permissions are updated first
    setTimeout(() => {
      dispatch({
        type: SG_ADD_ORGANIZATION_GROUP,
        payload,
      })
    }, 200)

    addToast("Contact added successfully", {
      appearance: "success",
      autoDismiss: true,
    })

    // Close modal and refresh
    setIsAddMemberModalOpen(false)
    setTimeout(() => {
      const orgId =
        get_auth?.organization_id || get_auth?.selectedOrganization?.id
      dispatch({
        type: SG_GET_ORGANIZATION_GROUP,
        payload: {
          page: currentPage,
          page_size: pageSize,
          organization: orgId,
          group_type: "permission",
        },
      })
      // Immediately refresh the manager cache used by Login Access filtering
      fetchAllManagerIds()
    }, 1000)
  }

  // Create new employee and then group
  const createEmployeeAndGroup = (email, firstNameInput = "", lastNameInput = "") => {
    // Extract name from email (basic approach)
    const firstName = (firstNameInput || "").trim();
    const lastName = (lastNameInput || "").trim();

    const employeePayload = {
      email: email,
      first_name: firstName,
      last_name: lastName,
      organization:
        get_auth?.organization_id || get_auth?.selectedOrganization?.id,
      // Core permissions as direct fields (matching ManagerPermissions.js expectations)

      survey_status: 1, // participate in survey = true
      account_type: 9, // regular user (not admin)
      // Optional defaults
      read_feedback: 0,
      survey_add_users: 0,
      survey_schedule: 0,
    }

    console.log("🔄 Creating new contact with permissions:", employeePayload)

    dispatch({
      type: SG_REGISTER_EMPLOYEE_CULTURE,
      payload: employeePayload,
    })

    addToast("Creating employee and group...", {
      appearance: "info",
      autoDismiss: true,
    })

    // We'll handle group creation in a useEffect that listens for employee creation
  }

  // Handle deleting member
  const handleDeleteMember = (groupId) => {
    setDeleteConfirmation({ isOpen: true, groupId })
  }

  const confirmDelete = () => {
    if (deleteConfirmation.groupId) {
      // Find the group's manager
      const group = organization_groups.find(
        (g) => g.id === deleteConfirmation.groupId
      )
      const managerId =
        group?.manager && typeof group.manager === "object"
          ? group.manager.id
          : group?.manager

      if (managerId) {
        // First, nullify manager permissions except participation (survey_status stays enabled)
        dispatch({
          type: SG_UPDATE_EMPLOYEE_AUDIT,
          payload: {
            id: managerId,
            enable_login: false,
            access_data_analytics: 0,
            access_subfactor_data: 0,
            read_feedback: 0,
            survey_add_users: 0,
            survey_schedule: 0,
            account_type: 9, // ensure not admin
            survey_status: 1, // keep participation enabled
          },
        })
      }

      // Then remove the group
      setTimeout(() => {
        dispatch({
          type: SG_DELETE_ORGANIZATION_GROUP,
          payload: { id: deleteConfirmation.groupId },
        })

        // Refresh manager cache so Login Access tab recalculates immediately
        fetchAllManagerIds()
      }, 200)

      addToast("Access removed successfully", {
        appearance: "success",
        autoDismiss: true,
      })
    }
    setDeleteConfirmation({ isOpen: false, groupId: null })
  }

  // Handle group selection for detail view
  const handleGroupSelect = (group) => {
    setSelectedGroup(group)
    setViewMode("detail")
  }

  // Handle going back to list view
  const handleBackToList = () => {
    setSelectedGroup(null)
    setViewMode("list")

    // Refresh organization groups when returning from detail view
    // This ensures we see any newly created groups
    console.log("🔄 Refreshing organization groups on return to list...")
    const orgId =
      get_auth?.organization_id || get_auth?.selectedOrganization?.id
    dispatch({
      type: SG_GET_ORGANIZATION_GROUP,
      payload: {
        page: currentPage,
        page_size: pageSize,
        organization: orgId,
        group_type: "permission",
      },
    })
    // Refresh the manager cache as well so Login Access recalculates immediately
    fetchAllManagerIds()

    // Also switch to Groups tab to show the newly created group
    setActiveTab(0)
  }

  // Sync page input value with current page when it changes externally
  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  // Handle page jump input
  const handlePageJump = (e, totalPages) => {
    if (e.key === "Enter") {
      const pageNumber = parseInt(pageInputValue, 10);
      if (
        !isNaN(pageNumber) &&
        pageNumber >= 1 &&
        pageNumber <= totalPages
      ) {
        setCurrentPage(pageNumber);
      } else {
        // Reset to current page if invalid
        setPageInputValue(currentPage.toString());
      }
    }
  };

  // Handle blur - reset to current page if invalid
  const handlePageInputBlur = (totalPages) => {
    const pageNumber = parseInt(pageInputValue, 10);
    if (
      isNaN(pageNumber) ||
      pageNumber < 1 ||
      pageNumber > totalPages
    ) {
      setPageInputValue(currentPage.toString());
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  // Conditional rendering based on view mode
  if (viewMode === "detail" && selectedGroup) {
    return <GroupDetail group={selectedGroup} onBack={handleBackToList} />
  }
  if (viewMode === 'bulk') {
    return <BulkImportManagers onBack={handleBackToList} />;
  }

  return (
    <Container>
      <AccessBreadcrumb />
      {/* Header Section */}
      <HeaderSection>
        <HeaderContent>
          <IconWrapper>
            <GroupIcon />
          </IconWrapper>
          <HeaderText>
            <Title>User Management</Title>
            <Subtitle>Manage user access and contact assignments</Subtitle>
          </HeaderText>
        </HeaderContent>
        <RightControls>
          <AddButton 
            onClick={() => {
              console.log('🔄 Opening modal, employees loaded:', employees.length);
              setIsAddMemberModalOpen(true);
            }}
            disabled={!employees.length}
          >
            <ButtonIcon>
              <PlusIcon />
            </ButtonIcon>
            <ButtonText>Invite User</ButtonText>
          </AddButton>
          <BulkImportButton onClick={() => setViewMode('bulk')} title="Bulk import users">
            Bulk Import
          </BulkImportButton>
          {activeTab === 1 && !allManagersLoading && getLoginEmployeeItems().length > 0 && (
            <SyncButton
              onClick={openSyncModal}
              title="Create groups for all login access contacts who are not managers"
            >
              Synchronize Managers
            </SyncButton>
          )}
        </RightControls>
      </HeaderSection>

      {/* Tabs - hidden by default; revealed after 20s or earlier if login accounts exist */}
      {shouldShowTabs &&
        !allManagersLoading &&
        getLoginEmployeeItems().length > 0 && (
          <TabsSection>
            <TabButton
              active={activeTab === 0}
              onClick={() => {
                setActiveTab(0)
                setCurrentPage(1)
                const orgId =
                  get_auth?.organization_id ||
                  get_auth?.selectedOrganization?.id
                const payload = {
                  page: 1,
                  page_size: pageSize,
                  organization: orgId,
                  group_type: "permission",
                }
                if (selectedManagerFilter?.id)
                  payload.manager = selectedManagerFilter.id
                dispatch({ type: SG_GET_ORGANIZATION_GROUP, payload })
              }}
            >
              User Access ({getGroupItems().length})
            </TabButton>
            <TabButton
              active={activeTab === 1}
              onClick={() => {
                setActiveTab(1)
                setCurrentPage(1)
                setSearchTerm("")
              }}
            >
              Login Access ({getLoginEmployeeItems().length})
            </TabButton>
          </TabsSection>
        )}

      {/* Members Table */}
      <TableSection>
        {/* Search Row - Always visible for searching contacts */}
        <SearchBarRow>
          <SearchContainer>
            <SearchInputBox
              type="text"
              placeholder="Search user by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => {
                if (searchTerm.trim() && searchResults.length > 0) {
                  setShowSearchDropdown(true)
                }
              }}
              onBlur={() => {
                // Delay hiding to allow clicks on dropdown items
                setTimeout(() => setShowSearchDropdown(false), 200)
              }}
            />
            {showSearchDropdown && (
              <SearchDropdown>
                {searchResults.map((item) => (
                  <SearchDropdownItem
                    key={item.id}
                    onClick={() => {
                      // If the selected employee is a manager, filter groups by that manager.
                      // Otherwise, open an individual permissions view (temporary group).
                      if (allManagerIds.has(item.data.id)) {
                        setSelectedManagerFilter(item.data);
                        setActiveTab(0);
                        const orgId = get_auth?.organization_id || get_auth?.selectedOrganization?.id;
                        dispatch({ type: SG_GET_ORGANIZATION_GROUP, payload: { page: 1, page_size: pageSize, organization: orgId, group_type: 'permission', manager: item.data.id } });
                        setCurrentPage(1);
                      } else {
                        const tempGroup = {
                          id: `temp_${item.data.id}`,
                          name: `${item.data.first_name || ''} ${item.data.last_name || ''}`.trim() || item.data.email,
                          manager: item.data.id,
                          members: [item.data],
                          isTemporary: true,
                        };
                        handleGroupSelect(tempGroup);
                      }
                      setSearchTerm('');
                      setShowSearchDropdown(false);
                    }}
                  >
                    <SearchResultName>
                      {item.manager.first_name} {item.manager.last_name}
                    </SearchResultName>
                    <SearchResultEmail>{item.manager.email}</SearchResultEmail>
                  </SearchDropdownItem>
                ))}
              </SearchDropdown>
            )}
          </SearchContainer>
          {selectedManagerFilter && (
            <ClearFilterButton
              onClick={() => {
                setSelectedManagerFilter(null)
                const orgId =
                  get_auth?.organization_id ||
                  get_auth?.selectedOrganization?.id
                dispatch({
                  type: SG_GET_ORGANIZATION_GROUP,
                  payload: {
                    page: 1,
                    page_size: pageSize,
                    organization: orgId,
                    group_type: "permission",
                  },
                })
                setCurrentPage(1)
              }}
            >
              Clear filter
            </ClearFilterButton>
          )}
        </SearchBarRow>
        {(() => {
          const currentItems = getCurrentTabItems()
          return currentItems.length > 0 ? (
            <GroupsTable>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>User Name</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {(activeTab === 0
                  ? currentItems // server paginated list
                  : currentItems.slice(
                      (currentPage - 1) * pageSize,
                      currentPage * pageSize
                    )
                ).map((item, index) => {
                  const manager = item.manager

                  return (
                    <ClickableTableRow
                      key={item.id}
                      index={index}
                      onClick={() => {
                        if (item.type === "group") {
                          handleGroupSelect(item.data)
                        } else {
                          // For login contacts, apply manager filter on groups
                          setSelectedManagerFilter(item.data)
                          const orgId =
                            get_auth?.organization_id ||
                            get_auth?.selectedOrganization?.id
                          dispatch({
                            type: SG_GET_ORGANIZATION_GROUP,
                            payload: {
                              page: 1,
                              page_size: pageSize,
                              organization: orgId,
                              group_type: "permission",
                              manager: item.data.id,
                            },
                          })
                          setCurrentPage(1)
                        }
                      }}
                      title={
                        item.type === "group"
                          ? "Click to view group details"
                          : "Click to manage individual permissions"
                      }
                    >
                      <TableCell>
                        {manager ? (
                          <EmployeeInfo>
                            <EmployeeName>
                              {manager.first_name} {manager.last_name}
                            </EmployeeName>
                            <EmployeeEmail>{manager.email}</EmployeeEmail>
                          </EmployeeInfo>
                        ) : (
                          <span>No user assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <ActionButtons>
                          {item.type === "group" ? (
                            <ActionButton
                              onClick={(e) => {
                                e.stopPropagation() // Prevent row click
                                handleDeleteMember(item.data.id)
                              }}
                              title="Remove Access"
                              danger
                            >
                              <DeleteIcon />
                            </ActionButton>
                          ) : (
                            <ActionButton
                              onClick={(e) => {
                                e.stopPropagation() // Prevent row click
                                // For login contacts, we could navigate to their individual permission page
                                const tempGroup = {
                                  id: `temp_${item.data.id}`,
                                  name: `${item.data.first_name} ${item.data.last_name} - Individual Access`,
                                  manager: item.data.id,
                                  members: [item.data],
                                  isTemporary: true,
                                }
                                handleGroupSelect(tempGroup)
                              }}
                              title="Manage permissions"
                            >
                              <EditIcon />
                            </ActionButton>
                          )}
                        </ActionButtons>
                      </TableCell>
                    </ClickableTableRow>
                  )
                })}
              </TableBody>
            </GroupsTable>
          ) : (
            <EmptyState>
              <EmptyIcon>
                <GroupIcon />
              </EmptyIcon>
              <EmptyTitle>No Members</EmptyTitle>
              <EmptyText>
                {activeTab === 0
                  ? "Add your first group to start managing team access and permissions."
                  : "No contacts with login access found."}
              </EmptyText>
              {activeTab === 0 && (
                <EmptyButton onClick={() => setIsAddMemberModalOpen(true)}>
                  <PlusIcon />
                  Add First Contact
                </EmptyButton>
              )}
            </EmptyState>
          )
        })()}
      </TableSection>

      {/* Pagination */}
      {(() => {
        const currentItems = getCurrentTabItems()

        if (activeTab === 0) {
          // Groups tab - reflect server pagination since results come from API with count
          const totalGroups = pagination?.count || currentItems.length
          const totalPages = Math.ceil(totalGroups / pageSize) || 1
          const startItem = totalGroups ? (currentPage - 1) * pageSize + 1 : 0
          const endItem = Math.min(currentPage * pageSize, totalGroups)

          return (
            totalGroups > 0 && (
              <PaginationSection>
                <PaginationInfo>
                  Showing {startItem} to {endItem} of {totalGroups} users
                </PaginationInfo>
                <PaginationControls>
                  <PaginationButton
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={!pagination?.previous}
                  >
                    Previous
                  </PaginationButton>
                  <PaginationInfo>
                    Page{" "}
                    <PageJumpInput
                      type="number"
                      min="1"
                      max={totalPages}
                      value={pageInputValue}
                      onChange={(e) => setPageInputValue(e.target.value)}
                      onKeyDown={(e) => handlePageJump(e, totalPages)}
                      onBlur={() => handlePageInputBlur(totalPages)}
                    />{" "}
                    of {totalPages}
                  </PaginationInfo>
                  <PaginationButton
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={!pagination?.next}
                  >
                    Next
                  </PaginationButton>
                </PaginationControls>
              </PaginationSection>
            )
          )
        } else {
          // Login Access tab - use client-side pagination
          const totalEmployees = currentItems.length
          const totalPages = Math.ceil(totalEmployees / pageSize)
          const startItem = (currentPage - 1) * pageSize + 1
          const endItem = Math.min(currentPage * pageSize, totalEmployees)

          return (
            totalEmployees > 0 && (
              <PaginationSection>
                <PaginationInfo>
                  Showing {startItem} to {endItem} of {totalEmployees} contacts
                </PaginationInfo>
                <PaginationControls>
                  <PaginationButton
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                  >
                    Previous
                  </PaginationButton>
                  <PaginationInfo>
                    Page{" "}
                    <PageJumpInput
                      type="number"
                      min="1"
                      max={totalPages}
                      value={pageInputValue}
                      onChange={(e) => setPageInputValue(e.target.value)}
                      onKeyDown={(e) => handlePageJump(e, totalPages)}
                      onBlur={() => handlePageInputBlur(totalPages)}
                    />{" "}
                    of {totalPages}
                  </PaginationInfo>
                  <PaginationButton
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </PaginationButton>
                </PaginationControls>
              </PaginationSection>
            )
          )
        }
      })()}

      {/* Modals */}
      <AddMemberModal
        isOpen={isAddMemberModalOpen}
        onClose={() => setIsAddMemberModalOpen(false)}
        onSave={handleCreateMember}
        employees={employees}
        organizationGroups={organization_groups}
        get_auth={get_auth}
      />

      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, groupId: null })}
        onConfirm={confirmDelete}
        title="Remove User Access"
        message="Are you sure you want to remove this user's access? This action cannot be undone."
        confirmText="Remove"
        cancelText="Cancel" 
      />

      {/* Synchronize Managers Modal */}
      {isSyncModalOpen && (
        <ModalOverlay
          onClick={() => {
            if (!syncRunning) setIsSyncModalOpen(false)
          }}
        >
          <SyncModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Synchronize Managers</ModalTitle>
              <CloseButton
                onClick={() => {
                  if (!syncRunning) setIsSyncModalOpen(false)
                }}
              >
                x
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <SyncStatus>
                {syncTotal > 0 ? (
                  <>
                    <div>
                      Processing {syncIndex}/{syncTotal}
                    </div>
                    {syncCurrent && (
                      <div>
                        Current: {syncCurrent.first_name}{" "}
                        {syncCurrent.last_name} ({syncCurrent.email})
                      </div>
                    )}
                  </>
                ) : (
                  <div>No login access contacts to synchronize.</div>
                )}
              </SyncStatus>
              <ProgressBar>
                <ProgressFill
                  style={{
                    width: `${
                      syncTotal
                        ? Math.min(
                            100,
                            Math.round((syncIndex / syncTotal) * 100)
                          )
                        : 0
                    }%`,
                  }}
                />
              </ProgressBar>
              {syncErrors.length > 0 && (
                <SyncErrors>
                  <div>Errors: {syncErrors.length}</div>
                </SyncErrors>
              )}
            </ModalBody>
            <ModalActions>
              {!syncRunning ? (
                <SaveButton onClick={() => setIsSyncModalOpen(false)}>
                  Close
                </SaveButton>
              ) : (
                <CancelButton
                  onClick={() => {
                    syncCancelledRef.current = true
                  }}
                >
                  Cancel
                </CancelButton>
              )}
            </ModalActions>
          </SyncModalContent>
        </ModalOverlay>
      )}
    </Container>
  )
}

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BulkImportButton = styled.button`
  padding: 12px 16px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #414141;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e9ecef;
    border-color: #476dfa;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 32px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 16px;
  color: #7e7e7e;
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #476dfa;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const ButtonIcon = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonText = styled.span`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: white;
`;

const TableSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SearchBarRow = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px 24px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  align-items: center;
`;

const SearchInputBox = styled.input`
  flex: 3;
  min-width: 400px;
  max-width: 600px;
  width: 100%;
  padding: 16px 20px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  font-family: "Poppins";
  font-size: 16px;
  font-weight: 400;
  transition: all 0.2s ease;
  min-height: 24px;

  &:focus {
    outline: none;
    border-color: #476dfa;
    box-shadow: 0 0 0 3px rgba(71, 109, 250, 0.1);
  }

  &::placeholder {
    color: #999;
  }

  @media (max-width: 768px) {
    min-width: 250px;
  }
`;

const ClearFilterButton = styled.button`
  flex-shrink: 0;
  padding: 16px 24px;
  border: 1px solid #e0e0e0;
  background: #f8f9fa;
  border-radius: 12px;
  font-family: "Poppins";
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  max-width: 150px;

  &:hover {
    background: #e9ecef;
    border-color: #476dfa;
    transform: translateY(-1px);
  }
`;

const TableHint = styled.div`
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  font-family: "Poppins";
  font-size: 14px;
  color: #7e7e7e;
  text-align: center;
`;

const GroupsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const ClickableTableRow = styled.tr`
  cursor: pointer;
  transition: all 0.2s;
  animation: slideInRow 0.3s ease-out;
  animation-fill-mode: both;

  &:hover {
    background: #f0f4ff;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(71, 109, 250, 0.1);
  }

  @keyframes slideInRow {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  ${props => props.index && `
    animation-delay: ${props.index * 0.05}s;
  `}
`;

const TableCell = styled.td`
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;

const GroupInfo = styled.div``;

const GroupName = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin-bottom: 4px;
`;

const GroupDescription = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const EmployeeInfo = styled.div``;

const EmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;

const EmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  background: ${props => props.isGroup ? '#dcfce7' : '#dbeafe'};
  color: ${props => props.isGroup ? '#166534' : '#1e40af'};
`;

const MembersInfo = styled.div``;

const MemberCount = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 8px;
`;

const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MemberItem = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: ${props => props.danger ? '#ff4444' : '#476dfa'};
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.danger ? '#cc3333' : '#3d5ce6'};
    transform: translateY(-1px);
  }
`;

const EmptyState = styled.div`
  padding: 64px 32px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #414141;
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0 0 24px 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const EmptyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #476dfa;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const SyncModalContent = styled(ModalContent)`
  max-width: 640px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #414141;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #7e7e7e;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;

  &:hover {
    background: #f0f0f0;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-family: "Poppins";
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-family: "Poppins";
  font-size: 14px;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-family: "Poppins";
  font-size: 14px;
  background: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
  }
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  background: #476dfa;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const SyncButton = styled.button`
  margin-left: 12px;
  padding: 12px 16px;
  background: #0ea5e9;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #0284c7;
  }
`;

// Pagination Styles
const PaginationSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  background: white;
  border-top: 1px solid #e0e0e0;
  margin-top: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PaginationInfo = styled.div`
  display: inline-flex;
  align-items: center;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  gap: 4px;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.disabled ? '#f0f0f0' : '#476dfa'};
  border: none;
  border-radius: 6px;
  color: ${props => props.disabled ? '#999' : 'white'};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
    transform: translateY(-1px);
  }
`;

const PageNumber = styled.div`
  padding: 8px 16px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  min-width: 40px;
  text-align: center;
`;

const PageJumpInput = styled.input`
  width: 50px;
  padding: 4px 6px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #414141;
  text-align: center;
  outline: none;
  transition: border-color 0.2s;
  display: inline-block;

  &:focus {
    border-color: #476dfa;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

// Confirmation Dialog Styles
const ConfirmationContent = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-width: 480px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ConfirmationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
`;

const ConfirmationTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #414141;
  margin: 0;
`;

const ConfirmationBody = styled.div`
  padding: 16px 24px 24px 24px;
`;

const ConfirmationMessage = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 16px;
  color: #7e7e7e;
  margin: 0;
  line-height: 1.5;
`;

const ConfirmationActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px 24px 24px;
`;

const ConfirmButton = styled.button`
  padding: 12px 24px;
  background: #dc3545;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #c82333;
    transform: translateY(-1px);
  }
`;

const SyncStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  font-family: "Poppins";
  color: #414141;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 10px;
  background: #f1f5f9;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #22c55e;
  transition: width 0.2s ease;
`;

const SyncErrors = styled.div`
  padding: 8px 12px;
  background: #fff7ed;
  border: 1px solid #ffedd5;
  color: #9a3412;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 12px;
`;

// Searchable Dropdown Styles
const SearchableDropdown = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownTrigger = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 48px;

  &:hover {
    border-color: #476dfa;
  }

  ${props => props.isOpen && `
    border-color: #476dfa;
    box-shadow: 0 0 0 3px rgba(71, 109, 250, 0.1);
  `}
`;

const SelectedOption = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Placeholder = styled.div`
  color: #999;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
`;

const DropdownArrow = styled.span`
  font-size: 12px;
  color: #7e7e7e;
  transition: transform 0.2s;
  transform: rotate(${props => props.isOpen ? '180deg' : '0deg'});
`;

const DropdownContent = styled.div`
  position: fixed;
  top: ${props => props.dropdownTop}px;
  left: ${props => props.dropdownLeft}px;
  width: ${props => props.dropdownWidth}px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 99999;
  animation: slideDown 0.2s ease-out;
  max-height: 300px;
  overflow: hidden;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  outline: none;

  &::placeholder {
    color: #999;
  }
`;

const DropdownList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const DropdownOption = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 2px;
  outline: none;

  &:hover {
    background: #f8f9fa;
  }

  &:focus {
    background: #e3f2fd;
    outline: none;
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const NoResults = styled.div`
  padding: 16px;
  text-align: center;
  color: #999;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
`;

// Tabs Styles
const TabsSection = styled.div`
  display: flex;
  margin-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const TabButton = styled.button`
  padding: 12px 24px;
  background: ${props => props.active ? '#f8f9fa' : 'transparent'};
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#476dfa' : 'transparent'};
  color: ${props => props.active ? '#476dfa' : '#7e7e7e'};
  font-family: "Poppins";
  font-weight: ${props => props.active ? '600' : '500'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f8f9fa;
    color: #476dfa;
  }
`;

// Employee Status Notes
const ExistingEmployeeNote = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  color: #166534;
  font-family: "Poppins";
  font-size: 12px;
  font-weight: 500;
`;

const NewEmployeeNote = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 6px;
  color: #92400e;
  font-family: "Poppins";
  font-size: 12px;
  font-weight: 500;
`;

const ManagerWarningNote = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-family: "Poppins";
  font-size: 12px;
  font-weight: 500;
`;

const EmailErrorNote = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-family: "Poppins";
  font-size: 12px;
  font-weight: 500;
`;

// Search Bar Styles
const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
`;

const SearchDropdownItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const SearchResultName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

const SearchResultEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  margin-top: 2px;
`;

export default DataAccessManagement;
